import React from 'react'
import { Table } from 'react-bootstrap'

export interface AppTableColumn<T = unknown> {
  key: string
  label: React.ReactNode
  render?: (item: T) => React.ReactNode
  className?: string
  headerClassName?: string
}

export interface AppTableProps<T = unknown> {
  /** Column definitions; used when data is provided. */
  columns?: AppTableColumn<T>[]
  /** Data rows; when set, body is built from columns. */
  data?: T[]
  /** Row key extractor for data. */
  rowKey?: (item: T) => string | number
  /** Custom table body (use when not using columns + data). */
  children?: React.ReactNode
  /** Custom thead (use when not using columns). */
  header?: React.ReactNode
  /** Table props passed to Bootstrap Table. */
  responsive?: boolean
  hover?: boolean
  size?: 'sm' | 'lg'
  bordered?: boolean
  striped?: boolean
  borderless?: boolean
  className?: string
}

/**
 * Reusable table: same look and behaviour across the app.
 * Use either (columns + data) or (header + children) for body.
 */
export function AppTable<T = unknown>({
  columns = [],
  data,
  rowKey,
  children,
  header,
  responsive = true,
  hover = true,
  size,
  bordered,
  striped,
  borderless,
  className = '',
}: AppTableProps<T>): React.ReactElement {
  const hasColumns = columns.length > 0
  const hasData = data != null
  const useHeader = header ?? (hasColumns && <thead><tr>{columns.map((c) => <th key={c.key} className={c.headerClassName}>{c.label}</th>)}</tr></thead>)
  const useBody = children ?? (hasColumns && hasData && (
    <tbody>
      {data!.map((item, idx) => (
        <tr key={rowKey ? rowKey(item) : idx}>
          {columns.map((col) => (
            <td key={col.key} className={col.className}>
              {col.render ? col.render(item) : (item as Record<string, unknown>)[col.key] as React.ReactNode}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  ))

  return (
    <Table
      responsive={responsive}
      hover={hover}
      size={size}
      bordered={bordered}
      striped={striped}
      borderless={borderless}
      className={className}
    >
      {useHeader}
      {useBody}
    </Table>
  )
}
